// Space or Track context: fixed sections, edited inline and saved directly (manual edits are never approval
// cards). Each section shows who changed it and when; the doc shows how full it is and its history.
// People who cannot edit read it, and can ask the assistant to propose a change.
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ContextDoc, ContextSectionView, ContextTargetKind } from '@dpw/shared';
import { api } from '../api';
import { useActions, useChange, useContextDoc, useContextHistory } from '../lib/queries';
import { AnimatePresence, motion } from 'motion/react';
import { useLinger } from '../lib/linger';
import { listItem } from '../lib/motion';
import { ApproveCard } from './ApproveCard';
import { Glide, Orb } from './motion';
import { localDay, localTime, shortDay } from '../lib/format';
import { ErrorNote, Loading } from './bits';
import { useAssistantPanel } from '../assistant/Panel';

const updatedLine = (at: string | null, by: { name: string } | null) =>
  at ? `Updated ${shortDay(localDay(at))}${by ? ` by ${by.name.split(' ')[0]}` : ''}` : null;

/** Plain text with light markdown: "- " lines become a list, blank lines split paragraphs. Never HTML. */
export function ContextText({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  const flush = () => { if (list.length) { blocks.push(<ul key={blocks.length}>{list.map((l, i) => <li key={i}>{l}</li>)}</ul>); list = []; } };
  for (const line of text.split('\n')) {
    const m = line.match(/^\s*[-*]\s+(.*)$/);
    if (m) { list.push(m[1]!); continue; }
    flush();
    if (line.trim()) blocks.push(<p key={blocks.length}>{line}</p>);
  }
  flush();
  return <div className="ctxtext">{blocks}</div>;
}

/** "About two pages, 60% used" */
export function ContextMeter({ chars, max }: { chars: number; max: number }) {
  const pct = Math.min(100, Math.round((chars / max) * 100));
  return (
    <div className="ctxmeter" role="meter" aria-valuemin={0} aria-valuemax={max} aria-valuenow={chars} aria-label="How full this context is">
      <span className="bar"><i style={{ width: `${pct}%` }} className={pct >= 90 ? 'hi' : ''} /></span>
      <span className="xs mut">About two pages, {pct}% used</span>
    </div>
  );
}

function Section({ doc, s, kind, id }: { doc: ContextDoc; s: ContextSectionView; kind: ContextTargetKind; id: string }) {
  const [draft, setDraft] = useState<string | null>(null);
  const save = useChange((text: string) => api.context.save(kind, id, s.key, text, doc.version));
  const editing = draft !== null;
  const after = editing ? doc.chars - s.text.length + draft.length : doc.chars;
  const over = after > doc.maxChars;
  const meta = updatedLine(s.updatedAt, s.updatedBy);
  return (
    <section className="ctxsec" aria-labelledby={`ctx-${kind}-${s.key}`}>
      <div className="ctxhead">
        <h3 id={`ctx-${kind}-${s.key}`}>{s.label}</h3>
        {meta && !editing && <span className="xs mut">{meta}</span>}
        {doc.canEdit && !editing && <button className="bt ghost sm" onClick={() => { save.reset(); setDraft(s.text); }}>{s.text ? 'Edit' : 'Add'}</button>}
      </div>
      {editing ? (
        <form className="col gap6" onSubmit={e => { e.preventDefault(); save.mutate(draft.replace(/\s+$/, ''), { onSuccess: () => setDraft(null) }); }}>
          <textarea className="inp" rows={Math.min(14, Math.max(4, draft.split('\n').length + 1))} value={draft} onChange={e => setDraft(e.target.value)}
            aria-label={s.label} autoFocus placeholder="Plain text. Start a line with - for a list." />
          <div className="rowc gap6">
            <button className="bt pri sm" disabled={save.isPending || over}>{save.isPending ? <span className="spin" /> : null}Save</button>
            <button type="button" className="bt sm" onClick={() => setDraft(null)}>Cancel</button>
            <span className={`xs ${over ? 'errtx' : 'mut'}`}>{over ? `Too long: the context would be ${after.toLocaleString('en-US')} of ${doc.maxChars.toLocaleString('en-US')} characters. Keep long material in Files.` : `${Math.round((after / doc.maxChars) * 100)}% of the context after saving`}</span>
          </div>
          {save.error ? <ErrorNote error={save.error} onReload={() => setDraft(null)} /> : null}
        </form>
      ) : s.text ? <ContextText text={s.text} /> : <div className="xs mut">Nothing yet.</div>}
    </section>
  );
}

function History({ kind, id }: { kind: ContextTargetKind; id: string }) {
  const h = useContextHistory(kind, id, true);
  if (h.isLoading) return <Loading />;
  if (h.error) return <ErrorNote error={h.error} />;
  if (!h.data?.length) return <div className="xs mut">No changes yet.</div>;
  return (
    <ol className="ctxhist">
      {h.data.map(e => (
        <li key={e.id}>
          <div className="rowc gap6"><b className="sm">{e.sectionLabel}</b>
            <span className="xs mut">{e.changedBy?.name ?? 'Someone'} · {shortDay(localDay(e.changedAt))} {localTime(e.changedAt)} · {e.via === 'assistant' ? 'approved from an assistant card' : 'edited by hand'} · version {e.version}</span>
          </div>
          <details>
            <summary className="xs">Before and after</summary>
            <div className="ctxdiff">
              <div><span className="k2">Before</span>{e.before ? <ContextText text={e.before} /> : <span className="xs mut">(empty)</span>}</div>
              <div><span className="k2">After</span>{e.after ? <ContextText text={e.after} /> : <span className="xs mut">(empty)</span>}</div>
            </div>
          </details>
        </li>
      ))}
    </ol>
  );
}

/** The editable context of one space or track. */
export function ContextPanel({ kind, id, spaceOf }: { kind: ContextTargetKind; id: string; /** The space, for "Tell the assistant". */ spaceOf?: string }) {
  const panel = useAssistantPanel();
  const q = useContextDoc(kind, id);
  const [showHistory, setShowHistory] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorNote error={q.error} />;
  const d = q.data;
  const updated = updatedLine(d.updatedAt, d.updatedBy);
  return (
    <Glide className="panel ctxdoc">
      <div className="ctxtop">
        <div className="col" style={{ gap: 4 }}>
          <b>{d.target.name} context</b>
          <span className="xs mut">Engagement facts the team and the assistant work from.{updated ? ` ${updated}.` : ''}</span>
        </div>
        <ContextMeter chars={d.chars} max={d.maxChars} />
        <button className={`bt sm${showHistory ? ' on' : ''}`} aria-pressed={showHistory} onClick={() => setShowHistory(!showHistory)}>{showHistory ? 'Back to the context' : 'History'}</button>
        {(d.canEdit || d.canPropose) && (
          <button type="button" className="bt sm" onClick={() => panel.open({ ...(kind === 'track' ? { trackId: id, spaceId: spaceOf ?? null, label: d.target.name } : { spaceId: id }), prompt: `Add to the ${d.target.name} context that ` })}>Tell the assistant</button>
        )}
      </div>
      {!showHistory && <Suggestions kind={kind} id={id} />}
      {!d.canEdit && (
        <div className="matters xs">
          {d.canPropose
            ? <>Only the director, an admin or the {kind === 'track' ? 'track or space' : 'space'} lead edits this. To suggest a change, tell the <Link className="link" to="/assistant">assistant</Link>: "add to the {d.target.name} context that ..."</>
            : <>You can read this context. The team keeps it up to date.</>}
        </div>
      )}
      {showHistory ? <History kind={kind} id={id} /> : d.sections.map(s => <Section key={s.key} doc={d} s={s} kind={kind} id={id} />)}
      <div className="xs mut">Long documents stay in Files; the assistant searches them only when needed.</div>
    </Glide>
  );
}

/** On a track: the space context, read only and collapsed, above the track's own. */
export function ParentContext({ spaceId }: { spaceId: string }) {
  const q = useContextDoc('space', spaceId);
  if (!q.data) return null;
  const filled = q.data.sections.filter(s => s.text);
  const updated = updatedLine(q.data.updatedAt, q.data.updatedBy);
  return (
    <details className="panel ctxparent">
      <summary><b>From {q.data.target.name}</b><span className="xs mut">{filled.length ? `${filled.length} section${filled.length === 1 ? '' : 's'}${updated ? ` · ${updated}` : ''}` : 'Nothing written yet'}</span></summary>
      {filled.map(s => (
        <section className="ctxsec" key={s.key}><div className="ctxhead"><h3>{s.label}</h3></div><ContextText text={s.text} /></section>
      ))}
      <Link className="xs link" to={`/spaces/${spaceId}?tab=context`}>Open the {q.data.target.name} context</Link>
    </details>
  );
}

/** Waiting context cards for this space or track, on top: suggested by the assistant, or asked for by someone. */
function Suggestions({ kind, id }: { kind: ContextTargetKind; id: string }) {
  const q = useActions('waiting');
  const mine = (q.data ?? []).filter(a => a.kind === 'update_context' && a.payload.target === kind && a.payload.targetId === id);
  const cards = useLinger(mine, a => a.status !== 'waiting');
  if (!cards.length) return null;
  return (
    <div className="ctxsug" aria-label="Suggested changes">
      <span className="k"><Orb size={9} />Suggested by the assistant</span>
      <AnimatePresence mode="popLayout" initial={false}>
        {cards.map(a => <motion.div key={a.id} layout variants={listItem} initial="initial" animate="animate" exit="exit"><ApproveCard action={a} variant="compact" /></motion.div>)}
      </AnimatePresence>
    </div>
  );
}
